// Founder approvals queue — ported from `frontend/app/approvals/index.tsx`
// (task-23 brief §6 item 1). Same data layer (`opsDB`), same tab structure
// (pending/waiting-on-owner/suspended vs. approved), rebuilt for the web in
// the portal's existing table/card idiom instead of React Native components.
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { opsDB, type ApprovedBusiness, type PendingBusiness, type ReviewStatus } from '@/lib/data/ops';
import { EmptyState, PageHeader, Segmented } from '@/ui/primitives';
import { inputClass } from '@/ui/classes';
import { SearchIcon } from '@/icons';
import { ShieldIcon } from '@/icons/nav';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/approvals/')({
  component: ApprovalsList,
});

function ageLabel(iso: string): string {
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

const WAITING_LABEL: Partial<Record<ReviewStatus, string>> = {
  rejected: 'Changes requested',
  info_requested: 'Info requested',
};

function docSummary(b: PendingBusiness): string {
  const uploaded = b.kycDocs.length;
  const approvedDocs = b.kycDocs.filter((d) => d.status === 'approved').length;
  return `${uploaded} doc${uploaded === 1 ? '' : 's'} uploaded${approvedDocs > 0 ? ` · ${approvedDocs} approved` : ''}`;
}

function PendingCard({ b }: { b: PendingBusiness }) {
  const badge =
    b.reviewStatus === 'suspended' ? (
      <span className="rounded-chip bg-tint-warning px-sm py-xs text-caption font-semibold text-danger">Suspended</span>
    ) : WAITING_LABEL[b.reviewStatus] ? (
      <span className="rounded-chip bg-tint-warning px-sm py-xs text-caption font-semibold text-warning">
        {WAITING_LABEL[b.reviewStatus]}
      </span>
    ) : (
      <span className="rounded-chip bg-tint-warning px-sm py-xs text-caption font-semibold text-warning">
        {ageLabel(b.createdAt)}
      </span>
    );

  return (
    <Link
      to="/approvals/$id"
      params={{ id: b.id }}
      className="block rounded-card border border-border bg-surface p-lg shadow-card"
    >
      <div className="flex items-center gap-sm">
        <h3 className="flex-1 truncate text-h3 font-bold text-ink">{b.name ?? 'Unnamed business'}</h3>
        {badge}
      </div>
      <p className="mt-xs text-caption text-ink-2">
        {(b.category ?? 'uncategorised')}
        {b.location ? ` · ${b.location}` : ''}
      </p>
      <p className="mt-sm text-body text-ink-2">
        Owner: {[b.ownerName, b.ownerEmail].filter(Boolean).join(' · ') || '—'}
      </p>
      <p className="mt-xs text-body text-ink-2">
        KYC: {docSummary(b)}
        {b.payout ? ` · settlement ${b.payout.method} (${b.payout.status})` : ' · no settlement details'}
      </p>
      {b.reviewReason ? <p className="mt-xs text-caption italic text-ink-3">"{b.reviewReason}"</p> : null}
    </Link>
  );
}

function ApprovedRow({ b }: { b: ApprovedBusiness }) {
  return (
    <Link
      to="/approvals/$id"
      params={{ id: b.id }}
      className="block rounded-card border border-border bg-surface p-lg shadow-card"
    >
      <div className="flex items-center gap-sm">
        <h3 className="flex-1 truncate text-h3 font-bold text-ink">{b.name ?? 'Unnamed business'}</h3>
        <span className="rounded-chip bg-tint-success px-sm py-xs text-caption font-semibold text-success">Live</span>
      </div>
      <p className="mt-xs text-caption text-ink-2">
        {(b.category ?? 'uncategorised')}
        {b.location ? ` · ${b.location}` : ''}
        {b.ownerName ? ` · ${b.ownerName}` : ''}
      </p>
    </Link>
  );
}

function ApprovalsList() {
  const { supabase } = useAuth();
  const [tab, setTab] = useState<'pending' | 'approved'>('pending');
  const [query, setQuery] = useState('');

  const pendingQuery = useQuery({
    queryKey: ['ops-pending', 'pending'],
    queryFn: async () => {
      const { data, error } = await opsDB.listPending(supabase);
      if (error) throw error;
      return data ?? [];
    },
  });

  const approvedQuery = useQuery({
    queryKey: ['ops-pending', 'approved'],
    queryFn: async () => {
      const { data, error } = await opsDB.listApproved(supabase);
      if (error) throw error;
      return data ?? [];
    },
    enabled: tab === 'approved',
  });

  const pending = pendingQuery.data ?? [];
  const needsReview = pending.filter((b) => b.reviewStatus === 'pending');
  const waitingOnOwner = pending.filter((b) => b.reviewStatus === 'rejected' || b.reviewStatus === 'info_requested');
  const suspended = pending.filter((b) => b.reviewStatus === 'suspended');

  const filteredApproved = useMemo(() => {
    const rows = approvedQuery.data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((b) => [b.name, b.category, b.location, b.ownerName].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [approvedQuery.data, query]);

  const forbidden = pendingQuery.isError;

  return (
    <section className="flex flex-col gap-lg">
      <PageHeader title="Approvals" lede="Every change to a business's approval status is logged with your reason." />

      <Segmented
        ariaLabel="Approval queue"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'pending', label: 'Pending', count: pendingQuery.data ? pending.length : undefined },
          { value: 'approved', label: 'Approved' },
        ]}
      />

      {forbidden ? (
        <p className="rounded-card border border-border bg-surface p-xl text-center text-body text-ink-2">
          Not available — this area is restricted, or the request failed.{' '}
          <button type="button" onClick={() => void pendingQuery.refetch()} className="text-canyon underline">
            Retry
          </button>
        </p>
      ) : tab === 'pending' ? (
        pendingQuery.isLoading ? (
          <PageLoading />
        ) : pending.length === 0 ? (
          <EmptyState
            icon={<ShieldIcon />}
            title="All caught up"
            body="No businesses are waiting for a decision. New registrations appear here the moment they are submitted."
          />
        ) : (
          <div className="flex flex-col gap-lg">
            {needsReview.length > 0 ? (
              <div className="flex flex-col gap-sm">
                <h2 className="text-h3 font-bold text-ink">Needs review <span className="text-ink-3">{needsReview.length}</span></h2>
                {needsReview.map((b) => (
                  <PendingCard key={b.id} b={b} />
                ))}
              </div>
            ) : null}
            {waitingOnOwner.length > 0 ? (
              <div className="flex flex-col gap-sm">
                <h2 className="text-h3 font-bold text-ink">Waiting on owner <span className="text-ink-3">{waitingOnOwner.length}</span></h2>
                {waitingOnOwner.map((b) => (
                  <PendingCard key={b.id} b={b} />
                ))}
              </div>
            ) : null}
            {suspended.length > 0 ? (
              <div className="flex flex-col gap-sm">
                <h2 className="text-h3 font-bold text-ink">Suspended <span className="text-ink-3">{suspended.length}</span></h2>
                {suspended.map((b) => (
                  <PendingCard key={b.id} b={b} />
                ))}
              </div>
            ) : null}
          </div>
        )
      ) : (
        <div className="flex flex-col gap-sm">
          <label className="relative block max-w-md">
            <span className="sr-only">Search live businesses</span>
            <SearchIcon className="pointer-events-none absolute left-md top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search live businesses"
              className={`${inputClass} pl-[38px]`}
            />
          </label>
          {approvedQuery.isLoading ? (
            <PageLoading label="Loading live businesses…" />
          ) : filteredApproved.length === 0 ? (
            <EmptyState title="No live businesses match" body="Try a shorter search, or clear it to see every live business." />
          ) : (
            filteredApproved.map((b) => <ApprovedRow key={b.id} b={b} />)
          )}
        </div>
      )}
    </section>
  );
}
